import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
} from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { useRenameReport } from "../../hooks/useRenameReport";
import type { PreflightReportSummary } from "../../types/preflight.types";

export interface RenameReportDialogProps {
  report: Pick<PreflightReportSummary, "id" | "name">;
  /** Custom trigger; defaults to a ghost "Rename" button. */
  trigger?: React.ReactNode;
}

/**
 * Permission-gated rename dialog (past-reports R6, KTD13). Optimistically patches the
 * report name across the detail + list caches and rolls back on error.
 */
export function RenameReportDialog({ report, trigger }: RenameReportDialogProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(report.name);
  const mutation = useRenameReport(report);

  useEffect(() => {
    if (open) setName(report.name);
  }, [open, report.name]);

  if (!permissions.PREFLIGHT_RENAME_REPORT.isOn) return null;

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== report.name;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button type="button" variant="ghost" size="sm">
            {t("preflight.rename.trigger")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("preflight.rename.title")}</DialogTitle>
          <DialogDescription>
            {t("preflight.rename.description")}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              mutation.mutate(trimmed, { onSuccess: () => setOpen(false) });
            }
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="rename-report-input">
              {t("preflight.rename.label")}
            </Label>
            <Input
              id="rename-report-input"
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              {t("preflight.rename.cancel")}
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending
                ? t("preflight.rename.saving")
                : t("preflight.rename.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
